// ###### IMPORTS
import React, { useState, useMemo } from 'react';
import { PlusCircleIcon, FolderIcon } from '@heroicons/react/24/solid';
import { deleteNote } from '../moduleServices/notepad_services';
import { useNotepadViewMode } from '../tab_settings/viewServices/useNotepadViewMode';
import { useCollapsedClusters } from './viewServices/useCollapsedClusters';
import ClusterSection from './viewComponents/cluster_section';
import DrawerNoteForm from '../tab_notes/viewComponents/drawer_form';
import SpinnerLoad from '../moduleComponents/spinner_load';

// ###### MAIN COMPONENT
// ##### REFACTORED: Simplified props and state management
const MainClusters = ({ notes, isNotesLoading, clusters, isClustersLoading, refreshNotes, updateNotesOptimistically }) => {
  const { viewMode } = useNotepadViewMode();
  const { collapsedClusters, toggleCollapse } = useCollapsedClusters();
  const [isModalOpen, setIsModalOpen] = useState(false);
  // ##### REFACTORED: Use selectedNoteId instead of editNote state
  const [selectedNoteId, setSelectedNoteId] = useState(null);

  // Derive selectedNote from notes array (single source of truth)
  const selectedNote = useMemo(() => {
    if (!selectedNoteId) return null;
    return notes.find(n => n.id === selectedNoteId) || null;
  }, [notes, selectedNoteId]);

  // ###### GROUP NOTES BY CLUSTER
  const groupNotesByCluster = () => {
    const grouped = {};
    
    // Group notes with clusters
    clusters.forEach(cluster => {
      grouped[cluster.id] = {
        cluster,
        notes: notes.filter(note => note.cluster?.id === cluster.id)
      };
    });
    
    // Group notes without cluster
    const notesWithoutCluster = notes.filter(note => !note.cluster);
    if (notesWithoutCluster.length > 0) {
      grouped['no-cluster'] = {
        cluster: null,
        notes: notesWithoutCluster
      };
    }
    
    return grouped;
  };

  // ###### HANDLERS
  const handleNewNote = () => {
    setSelectedNoteId(null);
    setIsModalOpen(true);
  };

  const handleEditNote = (note) => {
    setSelectedNoteId(note.id);
    setIsModalOpen(true);
  };

  const handleDeleteNote = async (noteId) => {
    try {
      await deleteNote(noteId);
      await refreshNotes();
      // If deleted note was selected, close drawer
      if (selectedNoteId === noteId) {
        setIsModalOpen(false);
        setSelectedNoteId(null);
      }
    } catch (error) {
      throw error;
    }
  };

  // ##### REFACTORED: Simplified - optimistic update already updates notes array
  const handleOptimisticUpdate = (updatedNoteData) => {
    if (updatedNoteData && updatedNoteData.id) {
      updateNotesOptimistically(updatedNoteData);
    }
  };

  // ##### REFACTORED: Simplified - just close drawer
  const handleDrawerClose = () => {
    setIsModalOpen(false);
    setSelectedNoteId(null);
  };


  // ###### LOADING STATE
  if ((isNotesLoading || isClustersLoading) && !isModalOpen) {
    return (
      <div className="p-2 flex justify-center items-center min-h-[400px]">
        <SpinnerLoad />
      </div>
    );
  }

  // ###### GROUP NOTES
  const groupedNotes = groupNotesByCluster();
  const clusterIds = Object.keys(groupedNotes);

  // ###### RENDER
  return (
    <>
      <div className="p-2 pt-6 md:pt-8 space-y-6 md:space-y-8">
        {clusterIds.length === 0 ? (
          <div className="flex flex-col items-center justify-center min-h-[400px]">
            <FolderIcon className="h-16 w-16 text-gray-300 mb-4" />
            <p className="text-gray-400">No clusters yet. Create your first cluster in Settings!</p>
          </div>
        ) : (
          clusterIds.map((clusterId) => {
            const { cluster, notes: clusterNotes } = groupedNotes[clusterId];
            // ##### DEFAULT: Collapsed on first appearance (true if not in state)
            const isCollapsed = collapsedClusters[clusterId] ?? true;
            
            return (
              <ClusterSection
                key={clusterId}
                cluster={cluster}
                notes={clusterNotes}
                isCollapsed={isCollapsed}
                onToggleCollapse={() => toggleCollapse(clusterId)}
                onNoteClick={handleEditNote}
                viewMode={viewMode}
              />
            );
          })
        )}
      </div>

      {/* FAB Button Add */}
      <button
        onClick={handleNewNote}
        className="fixed bottom-20 right-6 bg-yellow-500 hover:bg-yellow-600 text-white p-3 rounded-full shadow-lg"
      >
        <PlusCircleIcon className="h-8 w-8" />
      </button>

      {/* Drawer */}
      <DrawerNoteForm
        isOpen={isModalOpen}
        onClose={handleDrawerClose}
        onOptimisticUpdate={handleOptimisticUpdate}
        editNote={selectedNote}
        onDelete={handleDeleteNote}
        clusters={clusters}
      />
    </>
  );
};

export default MainClusters;
